import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, MapPin, Phone, Clock, ExternalLink, Search } from 'lucide-react';
import { trackEvent } from '../../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

/** PLZ-Werkstattsuche — echte OSM-Daten, keine erfundenen Einträge. */
export default function WorkshopSearch({ context = 'werkstatt', testId = 'workshop-search' }) {
  const [plz, setPlz] = useState('');
  const [radius, setRadius] = useState(15);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const search = async (e) => {
    e.preventDefault();
    if (!/^\d{5}$/.test(plz.trim())) {
      setError('Bitte eine gültige 5-stellige Postleitzahl eingeben.');
      return;
    }
    setError(null);
    setLoading(true);
    trackEvent('workshop_search_start', { context, plz: plz.trim(), radius });
    try {
      const res = await axios.get(`${API}/api/werkstatt/suche`, { params: { plz: plz.trim(), radius } });
      setData(res.data);
    } catch {
      setError('Suche derzeit nicht verfügbar. Bitte später erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-stone-200 p-6 sm:p-8" data-testid={testId}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-400 mb-4">Werkstatt in deiner Nähe finden</p>
      <form onSubmit={search} className="grid sm:grid-cols-[1fr_auto_auto] gap-3">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-stone-400" strokeWidth={2} />
          <input
            type="text"
            inputMode="numeric"
            maxLength={5}
            value={plz}
            onChange={(e) => setPlz(e.target.value.replace(/\D/g, ''))}
            placeholder="Postleitzahl — z. B. 70173"
            className="w-full rounded-xl border border-stone-300 bg-white pl-11 pr-4 py-3 text-[15px] text-stone-900 focus:border-stone-900 focus:outline-none transition-colors"
            data-testid={`${testId}-plz`}
          />
        </div>
        <select
          value={radius}
          onChange={(e) => setRadius(Number(e.target.value))}
          className="rounded-xl border border-stone-300 bg-white px-4 py-3 text-[14px] text-stone-700 focus:border-stone-900 focus:outline-none"
          aria-label="Umkreis"
          data-testid={`${testId}-radius`}
        >
          {[5, 10, 15, 25, 50].map((r) => <option key={r} value={r}>{r} km</option>)}
        </select>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-stone-900 px-6 py-3 text-[14px] font-semibold text-white hover:bg-emerald-600 disabled:bg-stone-300 transition-colors"
          data-testid={`${testId}-submit`}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" strokeWidth={2} />}
          Werkstätten suchen
        </button>
      </form>
      {error && <p className="mt-3 text-[13px] text-red-600" data-testid={`${testId}-error`}>{error}</p>}

      {data && (
        <div className="mt-6" data-testid={`${testId}-results`}>
          {data.count > 0 ? (
            <>
              <p className="text-[13px] text-stone-500 mb-3">{data.count} Werkstätten im Umkreis von {data.radius_km} km um {data.plz} — sortiert nach Datenqualität und Entfernung.</p>
              <div className="divide-y divide-stone-100 rounded-2xl border border-stone-200 overflow-hidden">
                {data.results.map((w, i) => (
                  <div key={`${w.name}-${i}`} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-4" data-testid={`${testId}-result-${i}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-[14.5px] font-semibold text-stone-900 truncate">{w.name}</p>
                      <p className="text-[12.5px] text-stone-500 truncate">{w.address || 'Adresse siehe Karte'} · {w.distance_km} km</p>
                      {w.opening_hours && (
                        <p className="text-[11.5px] text-stone-400 truncate flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" /> {w.opening_hours}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {w.phone && (
                        <a
                          href={`tel:${w.phone}`}
                          onClick={() => trackEvent('workshop_contact_click', { context, name: w.name })}
                          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3.5 py-1.5 text-[12.5px] font-semibold text-emerald-800 hover:bg-emerald-100 transition-colors"
                          data-testid={`${testId}-call-${i}`}
                        >
                          <Phone className="w-3.5 h-3.5" /> Anrufen
                        </a>
                      )}
                      {w.google_maps_url && (
                        <a
                          href={w.google_maps_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackEvent('workshop_result_click', { context, name: w.name })}
                          className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-3.5 py-1.5 text-[12.5px] font-semibold text-stone-700 hover:border-stone-900 transition-colors"
                          data-testid={`${testId}-map-${i}`}
                        >
                          Karte <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-[14px] text-stone-500" data-testid={`${testId}-empty`}>
              Für diese PLZ liegen aktuell keine verlässlichen Werkstattdaten vor. Versuche einen größeren Umkreis — oder lass dich über die kostenlose FuelX Fehlerdiagnose beraten.
            </p>
          )}
          <p className="mt-2 text-[11px] text-stone-400">{data.attribution}</p>
        </div>
      )}
    </div>
  );
}
